import React, { useEffect, useRef, useState } from 'react';

import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BlockIcon from '@mui/icons-material/Block';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CodeIcon from '@mui/icons-material/Code';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import {
  Alert,
  Box,
  Container,
  Typography,
  TextField,
  TextFieldProps,
  Button,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';

import BookingWidget from '../components/BookingWidget';
import ContactTurnstile, { ContactTurnstileHandle } from '../components/ContactTurnstile';
import Eyebrow from '../components/Eyebrow';
import { mono, tokens } from '../theme';

const workOptions = [
  'CMS or content platform',
  'Custom application',
  'Integration',
  'Not sure',
] as const;

const whenOptions = ['Now', '1 to 3 months', 'Later'] as const;

const assessmentSubjectSlug = 'cms-workflow-assessment';
const assessmentPrefill = 'Subject: CMS & Workflow Assessment\n\n';

function initialMessageFromSearch(search: string) {
  const params = new URLSearchParams(search);
  return params.get('subject') === assessmentSubjectSlug ? assessmentPrefill : '';
}

function ContactUs() {
  // The Turnstile sitekey is public. Its secret stays in the Worker environment.
  const sitekey = '0x4AAAAAAFl8Ck5gZaziBB56';
  const location = useLocation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(() => initialMessageFromSearch(location.search));
  const [work, setWork] = useState('');
  const [when, setWhen] = useState('');
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({ name: false, email: false, message: false });
  const sendingRef = useRef(false);
  const turnstileRef = useRef<ContactTurnstileHandle>(null);
  const nameValid = name.trim().length > 0 && name.trim().length <= 100;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const messageText = message.trim();
  const messageValid =
    messageText.length >= 10 &&
    messageText.length <= 5_000 &&
    messageText !== assessmentPrefill.trim();
  const valid = nameValid && emailValid && messageValid;

  useEffect(() => {
    const prefill = initialMessageFromSearch(location.search);
    if (prefill) {
      setMessage((current) => (current.trim().length === 0 ? prefill : current));
    }
  }, [location.search]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (sendingRef.current || !valid || !token) return;
    sendingRef.current = true;
    setStatus('sending');
    setError('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          ...(work ? { work } : {}),
          ...(when ? { when } : {}),
          token,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.ok !== true) throw new Error('Delivery failed');
      setName('');
      setEmail('');
      setMessage('');
      setWork('');
      setWhen('');
      setTouched({ name: false, email: false, message: false });
      setStatus('success');
    } catch {
      setStatus('error');
      setError('Your message could not be sent. Please try again.');
    } finally {
      sendingRef.current = false;
      turnstileRef.current?.reset();
      setToken('');
    }
  }

  return (
    <Box sx={{ pb: { xs: 2, md: 4 } }}>
      <Container sx={{ pt: { xs: 4, md: 6 } }}>
        {/* Page opener (Stitch Contact Us screen) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: { lg: 'flex-end' },
            justifyContent: 'space-between',
            gap: 4,
            pb: { xs: 4, md: 6 },
          }}
        >
          <Box sx={{ maxWidth: '48rem' }}>
            <Eyebrow>Talk with the principal</Eyebrow>
            <Typography
              component='h1'
              variant='h2'
              sx={{ mt: 2 }}
            >
              Contact us
            </Typography>
            <Typography
              variant='bodyLg'
              component='p'
              sx={{ mt: 2, maxWidth: '42rem' }}
            >
              Book a short introductory call, or send a message if you prefer to write. Either way,
              you talk with the person responsible for technical direction.
            </Typography>
          </Box>
          <Box
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: tokens.surfaceLow,
              border: `1px solid ${tokens.outlineVariant}`,
              minWidth: { lg: 280 },
              textAlign: { lg: 'right' },
            }}
          >
            <Typography
              variant='labelCaps'
              component='p'
              sx={{
                color: tokens.accentText,
                display: 'flex',
                alignItems: 'center',
                justifyContent: { lg: 'flex-end' },
                gap: 1,
              }}
            >
              <Box
                component='span'
                aria-hidden='true'
                sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.cyan }}
              />
              Who you talk to
            </Typography>
            <Typography
              variant='h5'
              component='p'
              sx={{ mt: 0.5, fontWeight: 700 }}
            >
              Principal-led practice
            </Typography>
            <Typography variant='bodySm'>
              The person responsible for technical direction.
            </Typography>
          </Box>
        </Box>

        <Box
          component='nav'
          aria-label='Ways to get in touch'
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 0.75,
            p: 0.75,
            mb: 3,
            maxWidth: { lg: 'calc((100% - 40px) * 7 / 12)' },
            borderRadius: 3,
            bgcolor: tokens.surfaceContainer,
          }}
        >
          {(
            [
              ['#write-heading', 'Write a message', AssignmentOutlinedIcon],
              ['#book', 'Pick a call time', CalendarMonthOutlinedIcon],
            ] as const
          ).map(([href, label, Icon]) => (
            <Box
              component='a'
              key={href}
              href={href}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                minHeight: 44,
                px: 2,
                borderRadius: 2,
                bgcolor: tokens.surfaceLowest,
                color: tokens.onSurface,
                fontWeight: 600,
                fontSize: '0.875rem',
                textDecoration: 'none',
                boxShadow: '0 1px 2px rgba(19, 27, 46, 0.06)',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <Icon
                aria-hidden='true'
                sx={{ fontSize: 18, color: tokens.accentText }}
              />
              {label}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(0, 5fr)' },
            columnGap: 5,
            rowGap: 3,
            alignItems: 'start',
            gridAutoRows: { lg: 'min-content' },
          }}
        >
          {/* Phones: call, form, then the supporting cards. Wide screens: form on the left,
            call and cards stacked on the right. */}
          <Box sx={{ gridColumn: { lg: 2 }, gridRow: { lg: 1 } }}>
            <BookingWidget />
          </Box>

          <Box
            sx={{
              gridColumn: { lg: 1 },
              gridRow: { lg: '1 / span 4' },
              display: 'grid',
              gap: 3,
              minWidth: 0,
            }}
          >
            <Box
              component='section'
              aria-labelledby='write-heading'
              sx={{ ...cardSx, p: { xs: 3, md: 5 } }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  columnGap: 2,
                  rowGap: 0.5,
                }}
              >
                <Typography
                  variant='labelCaps'
                  component='p'
                  sx={{ color: tokens.accentText }}
                >
                  Send a message
                </Typography>
                <Typography
                  variant='bodySm'
                  component='p'
                  sx={{ fontWeight: 600, color: tokens.onSurfaceVariant }}
                >
                  Read by the principal
                </Typography>
              </Box>
              <Typography
                id='write-heading'
                component='h2'
                variant='h4'
                sx={{ mt: 0.5, scrollMarginTop: '96px' }}
              >
                Prefer to write?
              </Typography>
              <Typography
                variant='body2'
                sx={{ mt: 1 }}
              >
                Tell us what needs to work better. Describe who uses the system, what is difficult
                today, and what you would like to change. If you have a target date or an existing
                platform, include that too. You do not need a finished specification.
              </Typography>
              <Typography
                variant='bodySm'
                component='p'
                sx={{ mt: 1, color: tokens.onSurfaceVariant }}
              >
                We use your message to discuss fit and possible next steps. Please leave out
                sensitive or confidential information.
              </Typography>

              <Box
                component='form'
                onSubmit={handleSubmit}
                sx={{
                  minWidth: 0,
                  mt: 3,
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                  columnGap: 2.5,
                  rowGap: 2.5,
                  '& .MuiFormHelperText-root.Mui-error': { color: tokens.errorText },
                }}
              >
                <LabelledField
                  id='contact-name'
                  label='Name'
                  required
                  name='name'
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    setStatus('idle');
                  }}
                  onBlur={() => setTouched((previous) => ({ ...previous, name: true }))}
                  error={touched.name && !nameValid}
                  helperText={
                    touched.name && !nameValid
                      ? name.trim().length > 100
                        ? 'Use no more than 100 characters.'
                        : 'Enter your name.'
                      : undefined
                  }
                  disabled={status === 'sending'}
                  inputProps={{ maxLength: 100 }}
                  autoComplete='name'
                />
                <LabelledField
                  id='contact-email'
                  label='Email'
                  required
                  name='email'
                  type='email'
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setStatus('idle');
                  }}
                  onBlur={() => setTouched((previous) => ({ ...previous, email: true }))}
                  error={touched.email && !emailValid}
                  helperText={
                    touched.email && !emailValid
                      ? 'Enter a valid email address, such as name@example.com.'
                      : 'Use an address such as name@example.com.'
                  }
                  disabled={status === 'sending'}
                  inputProps={{ maxLength: 254 }}
                  autoComplete='email'
                />
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <WorkChoice
                    value={work}
                    disabled={status === 'sending'}
                    onChange={(value) => {
                      setWork(value);
                      setStatus('idle');
                    }}
                  />
                </Box>
                <LabelledField
                  id='contact-when'
                  label='When'
                  select
                  SelectProps={{ native: true }}
                  name='when'
                  value={when}
                  onChange={(event) => {
                    setWhen(event.target.value);
                    setStatus('idle');
                  }}
                  helperText='Optional.'
                  disabled={status === 'sending'}
                >
                  <option value=''>Not specified</option>
                  {whenOptions.map((option) => (
                    <option
                      key={option}
                      value={option}
                    >
                      {option}
                    </option>
                  ))}
                </LabelledField>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <LabelledField
                    id='contact-message'
                    label='Message'
                    required
                    counter={`${message.trim().length} / 5,000`}
                    name='message'
                    value={message}
                    onChange={(event) => {
                      setMessage(event.target.value);
                      setStatus('idle');
                    }}
                    onBlur={() => setTouched((previous) => ({ ...previous, message: true }))}
                    error={touched.message && !messageValid}
                    helperText={
                      touched.message && !messageValid
                        ? message.trim().length > 5_000
                          ? 'Use no more than 5,000 characters.'
                          : 'Enter at least 10 characters, excluding leading and trailing spaces.'
                        : 'Describe your project or current challenge. 10–5,000 characters.'
                    }
                    disabled={status === 'sending'}
                    InputProps={{ inputComponent: 'textarea' }}
                    inputProps={{
                      minLength: 10,
                      maxLength: 5000,
                      style: { scrollMarginBlockEnd: '16px' },
                      onFocus: (event: React.FocusEvent<HTMLTextAreaElement>) =>
                        event.currentTarget.scrollIntoView({ block: 'nearest', behavior: 'auto' }),
                    }}
                    multiline
                    rows={4}
                  />
                </Box>

                <Box sx={{ gridColumn: '1 / -1' }}>
                  <ContactTurnstile
                    ref={turnstileRef}
                    sitekey={sitekey}
                    onToken={(value) => {
                      setToken(value);
                      if (value) setError('');
                    }}
                    onError={() => setError('Verification is unavailable.')}
                  />

                  <Typography
                    variant='bodySm'
                    component='p'
                    sx={{ mt: 1, mb: 2, color: tokens.onSurfaceVariant }}
                  >
                    If verification does not finish,{' '}
                    <Box
                      component='a'
                      href='mailto:info@scaledtech.consulting'
                      sx={{ color: tokens.accentText, overflowWrap: 'anywhere' }}
                    >
                      email info@scaledtech.consulting directly
                    </Box>
                    .
                  </Typography>

                  {status === 'success' && (
                    <Alert
                      severity='success'
                      role='status'
                      sx={{ mb: 2 }}
                    >
                      Message submitted. Thank you.
                    </Alert>
                  )}
                  {error && (
                    <Alert
                      severity='error'
                      role='alert'
                      sx={{ mb: 2 }}
                    >
                      {error}{' '}
                      <Box
                        component='a'
                        href='mailto:info@scaledtech.consulting'
                        sx={{
                          color: 'inherit',
                          fontWeight: 700,
                          textDecoration: 'underline',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        Email info@scaledtech.consulting directly
                      </Box>
                      .
                    </Alert>
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Button
                      variant='contained'
                      type='submit'
                      disabled={!valid || !token || status === 'sending'}
                      endIcon={<ArrowForwardIcon aria-hidden='true' />}
                    >
                      {status === 'sending' ? 'Sending…' : 'Send Message'}
                    </Button>
                    <Typography
                      variant='bodySm'
                      component='p'
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        color: tokens.onSurfaceVariant,
                      }}
                    >
                      <VerifiedUserOutlinedIcon
                        aria-hidden='true'
                        sx={{ fontSize: 16, color: tokens.accentText }}
                      />
                      Goes straight to the principal
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              component='section'
              aria-labelledby='how-we-work-heading'
              sx={{ ...cardSx, bgcolor: tokens.surfaceLow, p: { xs: 3, md: 3.5 } }}
            >
              <Typography
                id='how-we-work-heading'
                component='h2'
                variant='labelCaps'
                sx={{ color: tokens.accentText, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <HandshakeOutlinedIcon
                  aria-hidden='true'
                  sx={{ fontSize: 18 }}
                />
                How we work with you
              </Typography>
              <Box
                component='ul'
                sx={{
                  listStyle: 'none',
                  m: 0,
                  mt: 2,
                  p: 0,
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
                  gap: { xs: 2, md: 3 },
                }}
              >
                {workingTerms.map(([title, text]) => (
                  <li key={title}>
                    <Typography
                      component='h3'
                      variant='h6'
                      sx={{ fontSize: '0.9375rem' }}
                    >
                      {title}
                    </Typography>
                    <Typography
                      variant='bodySm'
                      component='p'
                      sx={{ mt: 0.5, color: tokens.onSurfaceVariant }}
                    >
                      {text}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
          </Box>

          <Box
            component='section'
            aria-labelledby='next-heading'
            sx={{ ...cardSx, gridColumn: { lg: 2 }, gridRow: { lg: 2 } }}
          >
            <Typography
              variant='labelCaps'
              component='p'
              sx={{ color: tokens.accentText }}
            >
              After you get in touch
            </Typography>
            <Typography
              id='next-heading'
              component='h2'
              variant='h4'
              sx={{ mt: 0.5, mb: 2.5 }}
            >
              What happens next
            </Typography>
            <Box
              component='ol'
              sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 2.5 }}
            >
              {nextSteps.map(([title, text], index) => (
                <Box
                  component='li'
                  key={title}
                  sx={{ display: 'flex', gap: 2 }}
                >
                  <Box
                    aria-hidden='true'
                    sx={{
                      flex: 'none',
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: tokens.surfaceContainer,
                      color: tokens.accentText,
                      display: 'grid',
                      placeItems: 'center',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box>
                    <Typography
                      component='h3'
                      variant='h6'
                      sx={{ fontSize: '0.9375rem' }}
                    >
                      {title}
                    </Typography>
                    <Typography variant='bodySm'>{text}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            component='section'
            aria-labelledby='assurance-heading'
            sx={{ ...cardSx, gridColumn: { lg: 2 }, gridRow: { lg: 3 } }}
          >
            <Typography
              id='assurance-heading'
              component='h2'
              variant='labelCaps'
              sx={{ color: tokens.accentText }}
            >
              Built and checked in house
            </Typography>
            <Typography
              variant='bodySm'
              component='p'
              sx={{ mt: 1, color: tokens.onSurfaceVariant }}
            >
              This site is an example of our work. The form, the booking calendar, and the
              accessibility options are our own code, built to the practices we use for client
              projects.
            </Typography>
            <Box
              component='ul'
              sx={{
                listStyle: 'none',
                m: 0,
                mt: 2,
                p: 0,
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.25,
              }}
            >
              {assurances.map(([label, Icon]) => (
                <Box
                  component='li'
                  key={label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    minHeight: 44,
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: tokens.surfaceContainer,
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: tokens.onSurface,
                  }}
                >
                  <Icon
                    aria-hidden='true'
                    sx={{ fontSize: 18, color: tokens.accentText, flexShrink: 0 }}
                  />
                  {label}
                </Box>
              ))}
            </Box>
            <Box
              component={Link}
              to='/about-us#security-and-data'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 2,
                color: tokens.accentText,
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              How we protect your data
              <ArrowForwardIcon
                aria-hidden='true'
                sx={{ fontSize: 16 }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              ...cardSx,
              bgcolor: tokens.surfaceLow,
              border: 0,
              gridColumn: { lg: 2 },
              gridRow: { lg: 4 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MailOutlineIcon
                aria-hidden='true'
                sx={{ fontSize: 18, color: tokens.accentText }}
              />
              <Typography
                component='h2'
                variant='labelMd'
                sx={{ fontWeight: 700 }}
              >
                Prefer email?
              </Typography>
            </Box>
            <Typography
              variant='bodySm'
              component='p'
              sx={{ mt: 1 }}
            >
              Write to us directly:
            </Typography>
            <Box
              component='a'
              href='mailto:info@scaledtech.consulting'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 1,
                color: tokens.accentText,
                fontFamily: mono,
                fontWeight: 600,
                fontSize: '0.875rem',
                overflowWrap: 'anywhere',
              }}
            >
              info@scaledtech.consulting
              <NorthEastIcon
                aria-hidden='true'
                sx={{ fontSize: 14 }}
              />
            </Box>
            <Typography
              variant='bodySm'
              component='p'
              sx={{ mt: 1.5, color: tokens.onSurfaceVariant }}
            >
              We&apos;re based in the United States and work remotely with teams around the world.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

const cardSx = {
  bgcolor: tokens.surfaceLowest,
  border: `1px solid ${tokens.outlineVariant}`,
  borderRadius: 3,
  p: { xs: 3, md: 4 },
  boxShadow: '0 1px 2px rgba(19, 27, 46, 0.04)',
} as const;

// The real process, from the Services page.
const nextSteps = [
  [
    'Discovery and assessment',
    'We discuss your goals, users, existing systems, and constraints, and identify what to address.',
  ],
  [
    'Scope and proposal',
    'If the work is a fit, we define a proposed scope, responsibilities, and approach for your review.',
  ],
  ['Agreed terms', 'Terms, timing, and costs are agreed for each engagement before work starts.'],
] as const;

// Plain statements of how engagements run (Stitch's "engagement code" box, with true items).
const workingTerms = [
  ['Principal-led', 'The person you talk to is the person responsible for technical direction.'],
  [
    'Platform-agnostic',
    'We extend what you run, integrate it, or help you move, whichever fits your team.',
  ],
  ['US based, worldwide', 'Working remotely with teams around the world.'],
] as const;

// Each one is described on About ("How we protect your data").
const assurances = [
  ['Signed, reviewed releases', VerifiedUserOutlinedIcon],
  ['Accessibility checks', AccessibilityNewIcon],
  ['No ad trackers', BlockIcon],
  ['Built in house', CodeIcon],
] as const;

// "What kind of work" as Stitch's chips: a radio group, so arrow keys move between options.
// Choosing the selected chip again clears it, since the question is optional.
function WorkChoice({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Box
      component='fieldset'
      sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}
    >
      <Typography
        component='legend'
        variant='labelMd'
        sx={{ p: 0, mb: 1, fontWeight: 600, color: tokens.primary }}
      >
        What kind of work
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {workOptions.map((option) => {
          const selected = value === option;
          return (
            <Box
              component='label'
              key={option}
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 36,
                px: 1.5,
                borderRadius: 1.5,
                fontSize: '0.8125rem',
                fontWeight: 600,
                cursor: disabled ? 'default' : 'pointer',
                bgcolor: selected ? tokens.navy : tokens.surfaceContainer,
                color: selected ? '#FFFFFF' : tokens.onSurface,
                // The border keeps the chosen chip distinct in dark mode too.
                border: `2px solid ${selected ? tokens.primary : 'transparent'}`,
                '&:has(input:focus-visible)': {
                  outline: `3px solid ${tokens.focus}`,
                  outlineOffset: '2px',
                },
              }}
            >
              <input
                type='radio'
                name='work'
                value={option}
                checked={selected}
                disabled={disabled}
                onChange={() => onChange(option)}
                onClick={() => {
                  if (selected) onChange('');
                }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  margin: 0,
                  opacity: 0,
                  cursor: 'inherit',
                }}
              />
              {option}
            </Box>
          );
        })}
      </Box>
      <Typography
        variant='bodySm'
        component='p'
        sx={{ mt: 1, color: tokens.onSurfaceVariant }}
      >
        Optional.
      </Typography>
    </Box>
  );
}

// Stitch form field: a visible label above a filled input, instead of a floating label.
function LabelledField({
  id,
  label,
  required,
  counter,
  ...props
}: TextFieldProps & { id: string; label: string; counter?: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
        <Typography
          component='label'
          id={`${id}-label`}
          htmlFor={id}
          variant='labelMd'
          data-error={props.error ? 'true' : undefined}
          // errorText is the site's error red: 7:1 or more in light and dark (Stitch's is 6.4:1).
          sx={{ fontWeight: 600, color: props.error ? tokens.errorText : tokens.primary }}
        >
          {label}
          {required && (
            <Box
              component='span'
              aria-hidden='true'
              data-required-marker=''
              sx={{ color: tokens.errorText }}
            >
              {' '}
              *
            </Box>
          )}
        </Typography>
        {counter && (
          <Typography
            variant='bodySm'
            aria-hidden='true'
            sx={{ color: tokens.onSurfaceVariant, fontFamily: 'inherit' }}
          >
            {counter}
          </Typography>
        )}
      </Box>
      <TextField
        id={id}
        required={required}
        fullWidth
        hiddenLabel
        {...props}
        sx={{
          '& .MuiOutlinedInput-root': { bgcolor: tokens.surfaceLowest, borderRadius: 2 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.outline },
          ...props.sx,
        }}
      />
    </Box>
  );
}

export default ContactUs;
